import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../CSS/LoginComponent.css';

const ForgetMe: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'lookup' | 'verify' | 'reset'>('lookup');
  const [formData, setFormData] = useState({
    input: '',
    inputType: 'username' as 'username' | 'email',
    verificationCode: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState<string | null>(null);

  // Backend API URL - using relative path for reverse proxy compatibility
  const API_BASE_URL = process.env.REACT_APP_API_URL || '';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
    if (success) {
      setSuccess(null);
    }
  };

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle lookup step - send username/email to backend
  const handleLookupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    // Validate email if input type is email
    if (formData.inputType === 'email' && !validateEmail(formData.input)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    // Validate input is not empty
    if (!formData.input.trim()) {
      setError(`Please enter a ${formData.inputType}`);
      setIsLoading(false);
      return;
    }

    try {
      // Production API endpoint
      // const response = await fetch(`${API_BASE_URL}/api/admin-forgot-password`, {
      const response = await fetch(`/api/admin-forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          input: formData.input.trim(),
          inputType: formData.inputType
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to process request');
      }

      if (data.success && data.data?.verificationCode) {
        setVerificationCode(data.data.verificationCode);
        setSuccess(`Verification code has been sent to ${data.data.email || 'your email'}`);
        setStep('verify');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred. Please try again.';
      setError(errorMessage);
      console.error('Forget password lookup error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle resend code
  const handleResendCode = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    setFormData(prev => ({ ...prev, verificationCode: '' }));

    try {
      // Production API endpoint
      // const response = await fetch(`${API_BASE_URL}/api/admin-forgot-password`, {
      const response = await fetch(`/api/admin-forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          input: formData.input.trim(),
          inputType: formData.inputType
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend verification code');
      }

      if (data.success && data.data?.verificationCode) {
        setVerificationCode(data.data.verificationCode);
        setSuccess(`New verification code has been sent to ${data.data.email || 'your email'}`);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred. Please try again.';
      setError(errorMessage);
      console.error('Resend code error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle verification code step
  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    // Compare codes (case-insensitive)
    if (formData.verificationCode.trim().toUpperCase() !== verificationCode?.toUpperCase()) {
      setError('Invalid verification code. Please try again.');
      return;
    }

    // Code matches, proceed to password reset
    setSuccess('Verification successful! Please set your new password.');
    setStep('reset');
  };

  // Handle password reset step
  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    // Validate passwords
    if (!formData.newPassword || formData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      setIsLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      // Production API endpoint
      // const response = await fetch(`${API_BASE_URL}/api/admin-reset-password`, {
      const response = await fetch(`/api/admin-reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          input: formData.input.trim(),
          inputType: formData.inputType,
          verificationCode: formData.verificationCode.trim(),
          newPassword: formData.newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      if (data.success) {
        setSuccess('Password reset successfully! Redirecting to login...');
        setTimeout(() => {
          navigate('/admin-dashboard');
        }, 2000);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred. Please try again.';
      setError(errorMessage);
      console.error('Password reset error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Render lookup form (step 1)
  const renderLookupForm = () => (
    <form className="login-form" onSubmit={handleLookupSubmit}>
      <h2 className="login-title">Forgot Password</h2>
      <p className="login-description">Enter your username or email to receive a verification code</p>
      
      <div className="login-field">
        <label htmlFor="inputType" className="login-label">
          Search By*
        </label>
        <select
          id="inputType"
          name="inputType"
          value={formData.inputType}
          onChange={handleInputChange}
          className="login-input"
          style={{ cursor: 'pointer' }}
        >
          <option value="username">Username</option>
          <option value="email">Email</option>
        </select>
      </div>

      <div className="login-field">
        <label htmlFor="input" className="login-label">
          {formData.inputType === 'email' ? 'Email Address*' : 'Username*'}
        </label>
        <input
          type={formData.inputType === 'email' ? 'email' : 'text'}
          id="input"
          name="input"
          value={formData.input}
          onChange={handleInputChange}
          className="login-input"
          placeholder={formData.inputType === 'email' ? 'Enter your email address' : 'Enter your username'}
          required
        />
      </div>

      {error && (
        <div className="login-error">
          {error}
        </div>
      )}

      {success && (
        <div style={{
          fontFamily: 'Lato, sans-serif',
          fontSize: '0.9rem',
          fontWeight: 400,
          color: '#2e7d32',
          backgroundColor: '#e8f5e9',
          border: '1px solid #c8e6c9',
          borderRadius: '4px',
          padding: '0.75rem',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          {success}
        </div>
      )}

      <button 
        type="submit" 
        className="login-submit-button"
        disabled={isLoading}
      >
        {isLoading ? 'Sending...' : 'Send Verification Code'}
      </button>

      <div style={{
        marginTop: '1.5rem',
        textAlign: 'center'
      }}>
        <button
          type="button"
          onClick={() => navigate('/admin-dashboard')}
          style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '0.9rem',
            fontWeight: 400,
            color: '#0E4877',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Back to Login
        </button>
      </div>
    </form>
  );

  // Render verification form (step 2)
  const renderVerifyForm = () => (
    <form className="login-form" onSubmit={handleVerifySubmit}>
      <h2 className="login-title">Enter Verification Code</h2>
      <p className="login-description">Please enter the 4-character code sent to your email</p>
      
      <div className="login-field">
        <label htmlFor="verificationCode" className="login-label">
          Verification Code*
        </label>
        <input
          type="text"
          id="verificationCode"
          name="verificationCode"
          value={formData.verificationCode}
          onChange={handleInputChange}
          className="login-input"
          placeholder="Enter verification code"
          maxLength={4}
          style={{ textTransform: 'uppercase', letterSpacing: '0.5rem', textAlign: 'center', fontSize: '0.85rem', fontWeight: 'bold' }}
          required
        />
      </div>

      <div style={{ marginTop: '0.5rem', marginBottom: '3vh',textAlign: 'center' }}>
        <button
          type="button"
          onClick={handleResendCode}
          disabled={isLoading}
          style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '0.9rem',
            fontWeight: 400,
            color: '#0E4877',
            background: 'none',
            border: 'none',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            textDecoration: 'underline',
            padding: 0,
            opacity: isLoading ? 0.5 : 1
          }}
        >
          Resend Code
        </button>
      </div>

      {error && (
        <div className="login-error">
          {error}
        </div>
      )}

      {success && (
        <div style={{
          fontFamily: 'Lato, sans-serif',
          fontSize: '0.9rem',
          fontWeight: 400,
          color: '#2e7d32',
          backgroundColor: '#e8f5e9',
          border: '1px solid #c8e6c9',
          borderRadius: '4px',
          padding: '0.75rem',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          {success}
        </div>
      )}

      <button 
        type="submit" 
        className="login-submit-button"
        disabled={isLoading}
      >
        Verify Code
      </button>

      <div style={{
        marginTop: '1.5rem',
        textAlign: 'center'
      }}>
        <button
          type="button"
          onClick={() => {
            setStep('lookup');
            setFormData(prev => ({ ...prev, verificationCode: '' }));
            setError(null);
            setSuccess(null);
          }}
          style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '0.9rem',
            fontWeight: 400,
            color: '#0E4877',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Back
        </button>
      </div>
    </form>
  );

  // Render password reset form (step 3)
  const renderResetForm = () => (
    <form className="login-form" onSubmit={handleResetSubmit}>
      <h2 className="login-title">Reset Password</h2>
      <p className="login-description">Enter your new password</p>
      
      <div className="login-field">
        <label htmlFor="newPassword" className="login-label">
          New Password*
        </label>
        <input
          type="password"
          id="newPassword"
          name="newPassword"
          value={formData.newPassword}
          onChange={handleInputChange}
          className="login-input"
          placeholder="Enter new password (min. 6 characters)"
          required
        />
      </div>

      <div className="login-field">
        <label htmlFor="confirmPassword" className="login-label">
          Confirm Password*
        </label>
        <input
          type="password"
          id="confirmPassword"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleInputChange}
          className="login-input"
          placeholder="Confirm new password"
          required
        />
      </div>

      {error && (
        <div className="login-error">
          {error}
        </div>
      )}

      {success && (
        <div style={{
          fontFamily: 'Lato, sans-serif',
          fontSize: '0.9rem',
          fontWeight: 400,
          color: '#2e7d32',
          backgroundColor: '#e8f5e9',
          border: '1px solid #c8e6c9',
          borderRadius: '4px',
          padding: '0.75rem',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          {success}
        </div>
      )}

      <button 
        type="submit" 
        className="login-submit-button"
        disabled={isLoading}
      >
        {isLoading ? 'Resetting...' : 'Reset Password'}
      </button>

      <div style={{
        marginTop: '1.5rem',
        textAlign: 'center'
      }}>
        <button
          type="button"
          onClick={() => navigate('/admin-dashboard')}
          style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '0.9rem',
            fontWeight: 400,
            color: '#0E4877',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Back to Login
        </button>
      </div>
    </form>
  );

  return (
    <div className="login-page">
      <div className="login-container">
        {step === 'lookup' && renderLookupForm()}
        {step === 'verify' && renderVerifyForm()}
        {step === 'reset' && renderResetForm()}
      </div>
    </div>
  );
};

export default ForgetMe;
